import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {I18n, Translate} from 'react-redux-i18n';
import {withRouter} from 'react-router-dom';
import TextField from '@material-ui/core/TextField';

import {Icon, InfoAdornment, AbstractFormComponent} from 'front-onceforall-core';
import {requiredValidator, isIdentificationNumberValid} from 'front-onceforall-core/dist/utils/validators';
import {getCountryByLanguage, COUNTRY} from 'front-onceforall-core/dist/utils/country';
import {isEmpty} from 'front-onceforall-core/dist/utils/strings';
import {GROUP_NUMBER_CODE, REGISTRATION_NUMBER_CODE, VAT_IDENTIFICATION_NUMBER_CODE} from 'front-onceforall-core/dist/utils/organisations';

import {captureError, consoleError} from '../../utils/log';

import authenticationService from '../../services/authentication/AuthenticationService';
import organisationService from '../../services/organisation/OrganisationService';
import subscriptionService from '../../services/subscription/SubscriptionService';
import identificationNumberService from '../../services/identificationNumber/IdentificationNumberService';
import userService from '../../services/user/UserService';

import {addError, addSuccess} from '../../actions/snackbars';
import {selectOrganisation} from '../../actions/organisation';
import {setOrganisationLogo} from '../../actions/organisationLogo';
import {setUser} from '../../actions/user';
import {setNewSubscription} from '../../actions/subscription';

import CountrySelector from '../../components/countrySelector';
import BusinessLineSelector from '../../components/businessLine/BusinessLineSelector';
import {ADDRESS_MAX_LENGTH} from '../../components/project/ProjectDescriptionForm';
import {pathResolver} from '../../utils/basePathHelper';

// Quick and dirty list filtering until somebody make a decision...
const SUPPORTED_REGISTRATION_COUNTRIES = [
  COUNTRY.ANDORRA,
  COUNTRY.AUSTRIA,
  COUNTRY.BELGIUM,
  // COUNTRY.BOLIVIA,
  COUNTRY.BULGARIA,
  COUNTRY.CROATIA,
  COUNTRY.CZECH_REPUBLIC,
  COUNTRY.DENMARK,
  COUNTRY.ESTONIA,
  COUNTRY.FRANCE,
  COUNTRY.GERMANY,
  COUNTRY.GREECE,
  COUNTRY.HUNGARY,
  COUNTRY.ICELAND,
  // COUNTRY.INDIA,
  COUNTRY.ITALY,
  // COUNTRY.IVORY_COAST,
  COUNTRY.LATVIA,
  COUNTRY.LITHUANIA,
  COUNTRY.LUXEMBOURG,
  // COUNTRY.MAURITIUS,
  // COUNTRY.MEXICO,
  COUNTRY.MONACO,
  COUNTRY.NETHERLANDS,
  COUNTRY.POLAND,
  COUNTRY.PORTUGAL,
  COUNTRY.ROMANIA,
  COUNTRY.SERBIA,
  COUNTRY.SLOVAKIA,
  COUNTRY.SLOVENIA,
  COUNTRY.SPAIN,
  COUNTRY.SWEDEN,
  COUNTRY.SWITZERLAND,
  COUNTRY.UNITED_KINGDOM
];

export class OrganisationRegisterView extends AbstractFormComponent {
  constructor(props) {
    super(props);

    this.state = {
      registrationNumberTypes: null,
      ...this.state,
      loading: false,
      organisation: null,
      types: []
    };

    this.isBackOffice = authenticationService.roles().isBackOffice();
  }

  componentDidMount() {
    this.fetchRegistrationNumberTypes();
    this.fetchIdentificationNumberTypes(); // Fetch for default Country on mount
  }

  getFormData(registrationNumber = '') {
    const country = this.state ? this.state.formData.country : getCountryByLanguage(this.props.currentLocale);

    return {
      registrationNumber,
      country,
      businessLine: 'CONSTRUCTION',
      fullName: '',
      addressLine: '',
      city: '',
      postCode: '',
      accessCode: ''
    };
  }

  requiredIfNewOrganisationValidator = value => (this.isOrganisationNew() ? requiredValidator(value) : null);

  getFormValidators() {
    return {
      registrationNumber: [
        requiredValidator,
        this.registrationNumberValidator.bind(this)
      ],
      country: this.requiredIfNewOrganisationValidator,
      businessLine: this.requiredIfNewOrganisationValidator,
      fullName: this.requiredIfNewOrganisationValidator,
      addressLine: this.requiredIfNewOrganisationValidator,
      city: this.requiredIfNewOrganisationValidator,
      postCode: this.requiredIfNewOrganisationValidator,
      accessCode: null
    };
  }

  fetchRegistrationNumberTypes() {
    identificationNumberService.findAllRegistrationNumberType()
      .then(types => {
        this.setState({
          registrationNumberTypes: types
            .filter(idNumber => SUPPORTED_REGISTRATION_COUNTRIES.includes(idNumber.country))
            .reduce((map, idNumber) => ({...map, [idNumber.country]: idNumber}), {})
        });
      });
  }

  isRegistrationNumberValid(value, inProgress = false) {
    return isIdentificationNumberValid(this.state.registrationNumberTypes[this.state.formData.country], value, inProgress);
  }

  registrationNumberValidator(value) {
    return this.isRegistrationNumberValid(value) ? null : 'error.invalidRegistrationNumber';
  }

  fetchIdentificationNumberTypes() {
    const country = this.state ? this.state.formData.country : getCountryByLanguage(this.props.currentLocale);
    identificationNumberService.findAllByCountry(country)
      .then(types => {
        const formData = {...this.state.formData};
        const formValidators = {...this.formValidators};
        const identificationNumberTypes = types.filter(identificationNumberType => identificationNumberType.code !== REGISTRATION_NUMBER_CODE);
        identificationNumberTypes
          .forEach(type => {
            formData[type.code] = '';
            formValidators[type.code] = this.identificationNumberValidator(type);
          });
        this.setState({
          formData,
          types: identificationNumberTypes
        });
        this.formValidators = formValidators;
      })
      .catch(() => this.props.actions.addError(<Translate value="error.generic"/>));
  }

  identificationNumberValidator(type) {
    const formatIfNewOrganisationValidator = value => (!this.isOrganisationNew() || isEmpty(value) || isIdentificationNumberValid(type, value) ? null : 'error.invalidIdentificationNumber');
    const typeValidators = [formatIfNewOrganisationValidator];
    if (type.required) {
      typeValidators.push(this.requiredIfNewOrganisationValidator);
    }

    return typeValidators;
  }

  handleChangeCallback(name, value) {
    if (name === 'country') {
      this.setState({
        formData: this.getFormData(),
        submitted: false
      }, () => {
        this.formValidators = this.getFormValidators();
        this.fetchIdentificationNumberTypes();
      });
    }

    if (name === 'registrationNumber') {
      const {country} = this.state.formData;
      if (this.isRegistrationNumberValid(value)) {
        this.searchOrganisation(value, country);
      }
    }
  }

  handleRegistrationNumberChange = event => {
    const {value} = event.target;
    if (!this.isRegistrationNumberValid(value, true)) {
      return;
    }

    this.handleChange('registrationNumber')(event);
  };

  searchOrganisation(registrationNumber, country) {
    this.setState({loading: true, submitted: false});

    organisationService.getByRegistrationNumberAndCountry(registrationNumber, country)
      .then(organisation => {
        const state = {...this.state};
        state.organisation = organisation;
        state.loading = false;
        if (organisation) {
          const {addressLine, city, postCode} = organisation.address;
          state.formData = {
            ...state.formData,
            businessLine: organisation.businessLine,
            fullName: organisation.fullName,
            addressLine,
            city,
            postCode
          };

          Object.keys(organisation.identificationNumbers).forEach(code => {
            state.formData[code] = organisation.identificationNumbers[code];
          });
        } else {
          state.types.forEach(type => {
            // When BackOffice user wants to create an Organisation not in ILG, pre-fill SIREN
            if (this.isTypeSiren(type)) {
              state.formData[type.code] = state.formData.registrationNumber.substring(0, 9);
            // When BackOffice user wants to create an Organisation not in ILG, pre-fill TVA Intracom
            } else if (this.isTypeTVAIntracom(type)) {
              state.formData[type.code] = `FR${String((12 + (3 * (Number(state.formData.registrationNumber.substring(0, 9)) % 97))) % 97).padStart(2, '0')}${state.formData.registrationNumber.substring(0, 9)}`;
            // When Country registration number is the VAT_IDENTIFICATION_NUMBER_CODE (same format), copy value
            } else if (this.isTypeSameFormatThanRegistrationNumber(type)) {
              state.formData[type.code] = state.formData.registrationNumber;
            } else {
              state.formData[type.code] = '';
            }
          });
          state.formData = {
            ...state.formData,
            ...this.getFormData(registrationNumber)
          };
        }

        this.setState(state);
      })
      .catch(e => {
        this.handleSubmitError(e);
      });
  }

  handleSubmitError(error) {
    if (error.response && error.response.status === 500) {
      this.props.actions.addError(<Translate value="error.generic"/>);
      consoleError('OrganisationRegisterView service error : ', error.response);
    } else {
      captureError('OrganisationRegisterView', error);
    }
  }

  shouldShowForm() {
    const {formData} = this.state;

    return (!this.state.loading || this.state.submitting) &&
      this.isRegistrationNumberValid(formData.registrationNumber) &&
      // Allow BackOffice users to create an Organisation not in ILG
      !(formData.country === COUNTRY.FRANCE && !this.state.organisation && !this.isBackOffice);
  }

  isOrganisationNew() {
    return !this.state.organisation || this.state.organisation.remote;
  }

  isTypeSameFormatThanRegistrationNumber(type) {
    return this.state.registrationNumberTypes[this.state.formData.country].format === type.format;
  }

  isTypeSiren(type) {
    return type.country === COUNTRY.FRANCE && type.code === GROUP_NUMBER_CODE;
  }

  isTypeTVAIntracom(type) {
    return type.country === COUNTRY.FRANCE && type.code === VAT_IDENTIFICATION_NUMBER_CODE;
  }

  handleNewOrganisationSubmitCallback = async organisation => {
    let redirectUrl = '/register-organisation-plan';

    const hasSubscription = await subscriptionService.subscriptionExistsByExternalOrganisation(organisation);

    if (hasSubscription) {
      redirectUrl = '/dashboard';
      this.props.actions.setNewSubscription(organisation.organisationId);
    }

    this.props.actions.addSuccess(<Translate value="organisation.createOrganisationSuccess"/>);

    this.props.actions.selectOrganisation(organisation);

    this.props.history.push(redirectUrl);
  };

  handleExistingOrganisationSubmitCallback = async (organisation, isAutoTie) => {
    let redirectUrl = '/global-dashboard?organisationRegistrationSuccessful=true';

    if (this.isBackOffice || isAutoTie) {
      redirectUrl = '/dashboard';
      this.props.actions.selectOrganisation(organisation);
    }

    this.props.history.push(redirectUrl);
  };

  async handleSubmitCallback() {
    try {
      const organisation = this.isOrganisationNew() ? await this.createOrganisation(this.state.formData) : this.state.organisation;

      const isAutoTie = this.isBackOffice ? false : await this.linkOrganisationAndGetAutoTie(organisation.organisationId, this.props.user.id);

      if (this.isOrganisationNew()) {
        this.handleNewOrganisationSubmitCallback(organisation);
      } else {
        this.handleExistingOrganisationSubmitCallback(organisation, isAutoTie);
      }
    } catch (error) {
      this.handleSubmitError(error);
    }
  }

  linkOrganisationAndGetAutoTie(organisationId, userId) {
    return new Promise(resolve => {
      userService.linkOrganisationOnRegister(organisationId, userId)
        .then(userOrganisation => resolve(userOrganisation.autoTie));
    });
  }

  createOrganisation = data => new Promise((resolve, reject) => {
    const organisation = {
      registrationNumber: data.registrationNumber,
      country: data.country,
      businessLine: data.businessLine,
      address: {
        addressLine: data.addressLine,
        city: data.city,
        postCode: data.postCode
      },
      accessCode: data.accessCode,
      fullName: data.fullName,
      identificationNumbers: {}
    };
    this.state.types.filter(type => !isEmpty(data[type.code]))
      .forEach(type => {
        organisation.identificationNumbers[type.code] = data[type.code];
      });

    organisationService.createOrganisation(organisation)
      .then(createdOrganisation => resolve(createdOrganisation))
      .catch(error => reject(error));
  });

  getIdentificationNumbersFields = () => this.state.types.map((type, index) => (this.isTypeSameFormatThanRegistrationNumber(type) ? null : (
    <div
      key={`id-number-${index}`}
      className="col-sm-6"
    >
      <TextField
        label={this.getFieldLabel(`identificationNumber.${type.country}.${type.code}.name`, type.required)}
        error={this.isOnError(type.code)}
        helperText={this.helperText(type.code)}
        id={type.code}
        name={type.code}
        value={this.state.formData[type.code]}
        onChange={this.handleChange(type.code)}
        disabled={!this.isOrganisationNew() || this.isTypeSiren(type)}
        InputProps={{
          endAdornment: (
            <InfoAdornment
              error={this.isOnError(type.code)}
              text={I18n.t(`identificationNumber.${type.country}.${type.code}.tooltip`)}
            />
          )
        }}
      />
    </div>
  )));

  getMessage = () => {
    const {organisation} = this.state;
    const {formData} = this.state;

    if (this.state.loading || !this.isRegistrationNumberValid(formData.registrationNumber)) {
      return {message: null};
    }

    if (organisation) {
      if (!organisation.enabled) {
        return {
          message: I18n.t('organisation.createOrganisationDeactivated', {organisationName: organisation.fullName}),
          isErrorMessage: true
        };
      }

      if (!organisation.status) {
        return {message: I18n.t('organisation.inactive'), isErrorMessage: true};
      }

      if (organisation.remote) {
        return {message: I18n.t('organisation.createOrganisationFoundInILG')};
      }

      if (this.props.user.userOrganisations.some(userOrganisation => organisation.organisationId === userOrganisation.organisationId)) {
        return {message: I18n.t('organisation.createOrganisationFoundAndLinked'), isErrorMessage: true};
      }

      return {message: I18n.t('organisation.createOrganisationFound')};
    }

    if (formData.country !== COUNTRY.FRANCE || this.isBackOffice) {
      return {message: null};
    }

    return {message: I18n.t('organisation.createOrganisationNotFound'), isErrorMessage: true};
  };

  render() {
    if (!this.state.registrationNumberTypes) {
      return null;
    }

    const {message, isErrorMessage = false} = this.getMessage();

    return (
      <div className="row justify-content-center row-eq-height">
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="row">
              <div className="col-12">
                <form
                  onSubmit={this.handleSubmit}
                  className="p-0"
                >
                  <h1 className="mt-0 mb-3"><Translate value="organisation.create.title"/></h1>

                  <div className="mb-5">
                    <Translate value="organisation.create.cautionNotice"/>
                  </div>

                  <div className="mb-5">
                    <Translate value="organisation.create.requiredNotice"/>
                  </div>

                  {message &&
                    <div className={`box flex-container mb-5 ${isErrorMessage && 'rejected error'}`}>
                      <span className="flex-shrink-0">
                        <Icon
                          icon={isErrorMessage ? 'cross' : 'info'}
                          size={20}
                        />
                      </span>
                      <span data-id="form-message">{message}</span>
                    </div>
                  }

                  <div className="row">
                    <div className="col-sm-4">
                      <CountrySelector
                        onChange={this.handleChange('country')}
                        value={this.state.formData.country}
                        countriesToShow={Object.keys(this.state.registrationNumberTypes)}
                      />
                    </div>

                    <div className="col-sm-8">
                      <TextField
                        autoFocus
                        error={this.isOnError('registrationNumber')}
                        helperText={this.helperText('registrationNumber')}
                        label={this.getFieldLabel(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.name`, true)}
                        id="registrationNumber"
                        name="registrationNumber"
                        onChange={this.handleRegistrationNumberChange}
                        value={this.state.formData.registrationNumber}
                        placeholder={I18n.t(
                          'organisation.create.step1Text',
                          {
                            idNumberName: I18n.t(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.name`)
                          }
                        )}
                        InputProps={{
                          readOnly: this.state.loading,
                          endAdornment: (
                            <InfoAdornment
                              text={I18n.t(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.tooltip`)}
                            />
                          )
                        }}
                      />
                    </div>
                  </div>
                  {this.shouldShowForm() &&
                    <>
                      <div className="row">
                        <div className="col-sm-6">
                          <BusinessLineSelector
                            onChange={this.handleChange('businessLine')}
                            country={this.state.formData.country}
                            value={this.state.formData.businessLine}
                            disabled={!this.isOrganisationNew()}
                          />
                        </div>
                        <div className="col-sm-6">
                          <TextField
                            label={this.getFieldLabel('organisation.accessCode.name')}
                            id="accessCode"
                            name="accessCode"
                            value={this.state.formData.accessCode}
                            onChange={this.handleChange('accessCode')}
                            disabled={!this.isOrganisationNew()}
                            InputProps={{
                              endAdornment: (
                                <InfoAdornment
                                  text={I18n.t('organisation.accessCode.tooltip')}
                                />
                              )
                            }}
                          />
                        </div>
                      </div>
                      <TextField
                        label={this.getFieldLabel('organisation.fullName', true)}
                        error={this.isOnError('fullName')}
                        helperText={this.helperText('fullName')}
                        id="fullName"
                        name="fullName"
                        value={this.state.formData.fullName}
                        onChange={this.handleChange('fullName')}
                        disabled={!this.isOrganisationNew()}
                      />
                      <TextField
                        label={this.getFieldLabel('address.addressLine', true)}
                        error={this.isOnError('addressLine')}
                        helperText={this.helperText('addressLine')}
                        id="addressLine"
                        name="addressLine"
                        multiline
                        rows={3}
                        rowsMax={20}
                        value={this.state.formData.addressLine}
                        onChange={this.handleChange('addressLine')}
                        disabled={!this.isOrganisationNew()}
                        inputProps={{
                          maxLength: ADDRESS_MAX_LENGTH
                        }}
                      />

                      <div className="row">
                        <div className="col-sm-4">
                          <TextField
                            label={this.getFieldLabel('address.postCode', true)}
                            error={this.isOnError('postCode')}
                            helperText={this.helperText('postCode')}
                            id="postCode"
                            name="postCode"
                            value={this.state.formData.postCode}
                            onChange={this.handleChange('postCode')}
                            disabled={!this.isOrganisationNew()}
                            inputProps={{
                              maxLength: ADDRESS_MAX_LENGTH
                            }}
                          />
                        </div>
                        <div className="col-sm-8">
                          <TextField
                            label={this.getFieldLabel('address.city', true)}
                            error={this.isOnError('city')}
                            helperText={this.helperText('city')}
                            id="city"
                            name="city"
                            value={this.state.formData.city}
                            onChange={this.handleChange('city')}
                            disabled={!this.isOrganisationNew()}
                            inputProps={{
                              maxLength: ADDRESS_MAX_LENGTH
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        {this.getIdentificationNumbersFields()}
                      </div>
                    </>
                  }

                  <div className="actions col-12 inline-container space-between">
                    <button
                      type="button"
                      className="large secondary"
                      onClick={this.props.history.goBack}
                    >
                      <Translate value="action.cancel"/>
                    </button>
                    <button
                      className="large cta inline-container"
                      type="submit"
                      disabled={this.state.submitting || this.state.loading || this.getMessage().isErrorMessage}
                    >
                      <Icon icon={(this.state.submitting || this.state.loading) ? 'loader' : 'arrow-right'}/>
                      <Translate value="action.continue"/>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="d-none d-lg-block col-lg-4">
          <div
            className="ml-5 d-flex align-items-center justify-content-between flex-column"
            style={{height: '100%', width: 'min-content', textAlign: 'center'}}
          >
            <div className="d-flex flex-column align-items-center">
              <h2><Translate value="organisation.create.step1"/></h2>
              <h3 className="m-0">
                <Translate
                  value="organisation.create.step1Text"
                  idNumberName={I18n.t(`identificationNumber.${this.state.formData.country}.${REGISTRATION_NUMBER_CODE}.name`)}
                />
              </h3>
              <img
                alt="register organisation"
                src={pathResolver('resources/svg/register-organisation-1.svg')}
                width="300"
              />
            </div>
            {this.shouldShowForm() &&
            <div className="d-flex flex-column align-items-center">
              <h2><Translate value="organisation.create.step2"/></h2>
              <h3 className="m-0"><Translate value="organisation.create.step2Text"/></h3>
              <img
                alt="register organisation"
                src={pathResolver('resources/svg/register-organisation-2.svg')}
                width="300"
              />
            </div>}
          </div>
        </div>
      </div>
    );
  }
}

OrganisationRegisterView.propTypes = {
  actions: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  currentLocale: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    user: state.user,
    currentLocale: state.i18n.locale
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      addError, addSuccess, selectOrganisation, setOrganisationLogo, setUser, setNewSubscription
    }, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrganisationRegisterView));
